import * as React from "react";
import {
  centreCropOf,
  nameFromFile,
} from "../../../domain/productComposer/files";
import {
  IMAGE_QUALITY,
  qualityOf,
} from "../../../domain/productComposer/images";
import { uploadProductImage } from "../../../services/productComposer";
import readImage from "./readImage";

const ACCEPTED_TYPES = new Set(["image/png", "image/jpeg"]);

const accepted = (fileList) =>
  [...fileList].filter((file) => ACCEPTED_TYPES.has(file.type));

const newImage = ({ imageId, file, source }) => ({
  imageId,
  status: "preparing",
  fileName: file.name,
  previewUrl: source,
  imageUrl: null,
  originalImageUrl: null,
  croppedOriginalImageUrl: null,
  mode: "crop",
  quality: null,
  error: null,
});

const uploadedChanges = ({ image, quality }) => ({
  status: "uploaded",
  imageUrl: image.imageUrl,
  originalImageUrl: image.originalImageUrl,
  croppedOriginalImageUrl: image.croppedOriginalImageUrl,
  quality,
  error: null,
});

/* Files stay here, keyed by image id, rather than in the composer's state: a File is not
 * plain data, and they are only needed to retry or re-edit an upload. */
export default function useBatchUploader({
  dispatch,
  productTypeId,
  aspectRatio,
  requirements,
}) {
  const files = React.useRef(new Map());
  const nextId = React.useRef(1);
  const [editingId, setEditingId] = React.useState(null);

  const update = React.useCallback(
    (imageId, changes) => dispatch({ type: "updateImage", imageId, changes }),
    [dispatch],
  );

  const upload = React.useCallback(
    async (imageId) => {
      const { file, edit } = files.current.get(imageId);
      const result = await uploadProductImage({
        file,
        productTypeId,
        onProgress: (percent) => update(imageId, { percent }),
        ...edit,
      });

      update(
        imageId,
        result.ok
          ? uploadedChanges(result)
          : { status: "failed", error: result.message },
      );
    },
    [productTypeId, update],
  );

  const prepare = React.useCallback(
    async (imageId) => {
      const entry = files.current.get(imageId);
      const { natural } = await readImage(entry.source);
      const crop = centreCropOf({ ...natural, aspectRatio });
      const quality = qualityOf({ requirements, ...crop });

      files.current.set(imageId, { ...entry, edit: { mode: "crop", crop } });

      const isTooSmall = quality === IMAGE_QUALITY.TOO_SMALL;

      update(imageId, {
        status: isTooSmall ? "too-small" : "uploading",
        quality,
      });

      return isTooSmall ? undefined : upload(imageId);
    },
    [aspectRatio, requirements, update, upload],
  );

  const register = React.useCallback((file) => {
    const imageId = `new-${nextId.current}`;
    nextId.current += 1;
    const source = URL.createObjectURL(file);
    files.current.set(imageId, { file, source, edit: null });

    return newImage({ imageId, file, source });
  }, []);

  const addToCard = React.useCallback(
    (cardId, fileList) =>
      accepted(fileList).forEach((file) => {
        const image = register(file);
        dispatch({ type: "addImage", cardId, image });
        prepare(image.imageId);
      }),
    [dispatch, prepare, register],
  );

  const addAsCards = React.useCallback(
    (fileList) =>
      accepted(fileList).forEach((file) => {
        const image = register(file);
        dispatch({
          type: "addCardWithImage",
          name: nameFromFile(file.name),
          image,
        });
        prepare(image.imageId);
      }),
    [dispatch, prepare, register],
  );

  const saveEdit = React.useCallback(
    ({ quality, ...edit }) => {
      const imageId = editingId;
      files.current.set(imageId, { ...files.current.get(imageId), edit });
      setEditingId(null);
      update(imageId, {
        status: "uploading",
        mode: edit.mode,
        quality,
        error: null,
      });
      upload(imageId);
    },
    [editingId, update, upload],
  );

  const retry = React.useCallback(
    (imageId) => {
      update(imageId, { status: "uploading", error: null, percent: null });
      upload(imageId);
    },
    [update, upload],
  );

  return {
    addToCard,
    addAsCards,
    retry,
    canEdit: (imageId) => files.current.has(imageId),
    startEdit: setEditingId,
    cancelEdit: () => setEditingId(null),
    saveEdit,
    editingSource: editingId ? files.current.get(editingId)?.source : null,
  };
}
